import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./StockCountPage.scss";
import * as putAwayService from "../../Services/Http/PutAwayService";
import { toast } from "react-toastify";
import { BlockerFunction, useBlocker, useNavigate } from "react-router";
import { dataContext } from "../../Context/contextProvider";
import { PutAwayScanResponse } from "../../Models/PutAwayScanResponse";
import { Button, Modal } from "react-bootstrap";
import { TransHeaderResponse } from "../../Models/TransHeaderResponse";
import { PutAwayListResponse } from "../../Models/PutAwayListResponse";
import { BooleanResponse } from "../../Models/BooleanResponse";
import { ValidCountLocation } from "../../Models/ValidCountLocation";
import { PutAwayScanItem } from "../../Models/PutAwayScanItem";

interface Props {}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const questionSound = new Audio();
questionSound.src = "/assets/sounds/question.mp3";
questionSound.load();

const getTodayLocalDate = (): string => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

// Qty is editable for non-serial stock (N) and serial modes 1-4 (quantity-based);
// strict per-serial lines (e.g. mode Y) keep a fixed qty.
const isQtyEditable = (mode?: string): boolean => {
  return mode === "N" || mode === "1" || mode === "2" || mode === "3" || mode === "4";
};

// Temporarily disabled: warn when scanning a different stockcode into a bin that already
// has stock. Set back to true to re-enable.
const BIN_MIXED_STOCK_WARNING_ENABLED = false;

const StockCountPage = (props: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const searchBoxInputRef = useRef<HTMLInputElement>(null);
  const [currentData, setCurrentData] = useState<PutAwayListResponse | null>(null);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [locations, setLocations] = useState<ValidCountLocation[]>([]);
  const [scanWarningCount, setScanWarningCount] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<ValidCountLocation | null>(null);
  const [locationModalOpen, setLocationModalOpen] = useState<boolean>(false);
  const [batchDate, setBatchDate] = useState<string>(getTodayLocalDate());
  const [batchDescription, setBatchDescription] = useState<string>("");
  const [qtyModalOpen, setQtyModalOpen] = useState<boolean>(false);
  const [binWarningOpen, setBinWarningOpen] = useState<boolean>(false);
  const [pendingItem, setPendingItem] = useState<PutAwayScanItem | null>(null);
  const [pendingBarcode, setPendingBarcode] = useState<string>("");
  const [pendingQty, setPendingQty] = useState<string>("");
  // "scan" = confirming a freshly previewed scan (commits a new line);
  // "edit" = changing the qty of an existing line from the list.
  const [pendingMode, setPendingMode] = useState<"scan" | "edit">("scan");
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // The bin the user has already confirmed "Yes" to mixing stockcodes in - don't warn again for it.
  // Cleared whenever a new bin is scanned.
  const acknowledgedBinRef = useRef<string>("");
  const navigate = useNavigate();
  const dataStore = useContext(dataContext);

  useLayoutEffect(() => {
    if (searchBoxInputRef.current) {
      searchBoxInputRef.current.focus();
    }
  }, []);

  const refreshItems = useCallback(async (): Promise<void> => {
    try {
      setSubmitDisabled(true);
      const data: PutAwayListResponse = await putAwayService.getPutAwayListAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
      setCurrentData(data);
    } catch (error: any) {
      setCurrentData(null);
      toast.error(error.message || error, { autoClose: false });
    } finally {
      setSubmitDisabled(false);
    }
  }, [dataStore.dbKey, dataStore.serverUrl]);

  // On entering the screen, keep a previously scanned bin only if items were scanned to it.
  // If a bin was left set with no items, clear it so the user has to scan a bin again.
  const initLoad = useCallback(async (): Promise<void> => {
    try {
      setSubmitDisabled(true);
      const data: PutAwayListResponse = await putAwayService.getPutAwayListAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
      const hasRows = !!data?.rows && data.rows.length > 0;
      if (data?.lastBinCode && !hasRows) {
        await putAwayService.deletePutAwayListAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
        setCurrentData(null);
      } else {
        setCurrentData(data);
      }
    } catch (error: any) {
      setCurrentData(null);
      toast.error(error.message || error, { autoClose: false });
    } finally {
      setSubmitDisabled(false);
    }
  }, [dataStore.dbKey, dataStore.serverUrl]);

  useEffect(() => {
    initLoad();
  }, [initLoad]);

  const loadLocations = useCallback(async (): Promise<void> => {
    try {
      const data: ValidCountLocation[] = await putAwayService.getValidCountLocationsAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
      setLocations(data || []);
      // If there's only one valid location, select it automatically; otherwise restore the
      // last location used (cached per server/company) if it's still in the list.
      if (data && data.length === 1) {
        setSelectedLocation(data[0]);
      } else if (data && data.length > 1) {
        const cached = localStorage.getItem(`stockcount_last_location_${dataStore.serverUrl || ""}_${dataStore.dbKey || ""}`);
        if (cached) {
          const match = data.find((l) => String(l.locationNo) === cached);
          if (match) {
            setSelectedLocation(match);
          }
        }
      }
    } catch (error: any) {
      toast.error(error.message || error, { autoClose: false });
    }
  }, [dataStore.dbKey, dataStore.serverUrl]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const loadScanWarningCount = useCallback(async (): Promise<void> => {
    try {
      const count: number | null = await putAwayService.getScanWarningCountAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
      setScanWarningCount(count);
    } catch (error: any) {
      // No warning configured / unavailable - leave it off rather than blocking the page.
      setScanWarningCount(null);
    }
  }, [dataStore.dbKey, dataStore.serverUrl]);

  useEffect(() => {
    loadScanWarningCount();
  }, [loadScanWarningCount]);

  const handleSelectLocation = (location: ValidCountLocation) => {
    setSelectedLocation(location);
    // Remember the last location used (per server/company) so it's pre-selected next time.
    localStorage.setItem(`stockcount_last_location_${dataStore.serverUrl || ""}_${dataStore.dbKey || ""}`, String(location.locationNo));
    setLocationModalOpen(false);
  };

  const onChangeLocation = () => {
    setLocationModalOpen(true);
  };

  // Open the qty popup to edit an existing line's quantity. Close leaves it unchanged;
  // confirm sets the new qty on that line.
  const openEditQty = (item: PutAwayScanItem) => {
    setPendingItem(item);
    setPendingBarcode("");
    setPendingMode("edit");
    setPendingQty(String(item.qty ?? ""));
    setQtyModalOpen(true);
  };

  // Commit a previewed scan (commit=true). qty is only used for stock-code lines; serials
  // ignore it (their qty comes from the serial lookup).
  const commitScan = useCallback(
    (barcode: string, qty?: number): void => {
      const focusSearch = () => {
        searchBoxInputRef.current?.focus();
      };
      putAwayService
        .scanPutAwayItemAPI(barcode, dataStore.serverUrl || "", dataStore.dbKey || "", true, true, true, qty, selectedLocation?.locationNo)
        .then((scanResponse: PutAwayScanResponse) => {
          if (!scanResponse || scanResponse.hasError) {
            errorSound.play();
            const errMsg = scanResponse && scanResponse.errorMessage ? scanResponse.errorMessage : "Could not add item";
            toast.error(errMsg, { autoClose: 1500 });
            return;
          }
          return refreshItems();
        })
        .then(focusSearch)
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
          focusSearch();
        });
    },
    [dataStore.serverUrl, dataStore.dbKey, refreshItems, selectedLocation],
  );

  const onConfirmQty = () => {
    const mode = pendingMode;
    const item = pendingItem;
    const barcode = pendingBarcode;
    const qtyNum = parseInt(pendingQty, 10);
    if (!pendingQty.trim() || isNaN(qtyNum) || qtyNum < 1) {
      errorSound.play();
      toast.error("Please enter a quantity", { autoClose: 1500 });
      qtyInputRef.current?.focus();
      return;
    }
    setQtyModalOpen(false);
    setPendingItem(null);
    setPendingBarcode("");
    setPendingQty("");
    setPendingMode("scan");
    if (mode === "edit") {
      // Editing an existing line: set its qty directly.
      if (item && item.id && qtyNum !== item.qty) {
        putAwayService
          .updateItemQtyAPI(item.id, qtyNum, dataStore.serverUrl || "", dataStore.dbKey || "")
          .then((resp: BooleanResponse) => {
            if (!resp || !resp.isTrue) {
              toast.error("Could not update quantity", { autoClose: 1500 });
            }
            return refreshItems();
          })
          .then(() => searchBoxInputRef.current?.focus())
          .catch((error: any) => {
            toast.error(error.message || error, { autoClose: false });
            searchBoxInputRef.current?.focus();
          });
      } else {
        searchBoxInputRef.current?.focus();
      }
      return;
    }
    // Scan mode: nothing was added during preview. Commit the scan now with the entered qty.
    commitScan(barcode, qtyNum);
  };

  const onCloseQty = () => {
    // Close changes nothing: a previewed scan is discarded, an edited line keeps its qty.
    setQtyModalOpen(false);
    setPendingItem(null);
    setPendingBarcode("");
    setPendingQty("");
    setPendingMode("scan");
    searchBoxInputRef.current?.focus();
  };

  const onConfirmBinWarning = () => {
    // Yes - the user accepts mixing stockcodes in this bin; remember the bin so we don't warn
    // again for further scans to it.
    acknowledgedBinRef.current = pendingItem?.binCode || "";
    const item = pendingItem;
    const barcode = pendingBarcode;
    setBinWarningOpen(false);
    if (item?.serialNo && item?.qty) {
      // Serial line with a default qty - commit it straight away.
      setPendingItem(null);
      setPendingBarcode("");
      setPendingMode("scan");
      commitScan(barcode);
    } else {
      // Stock-code line, or a serial with no default qty - still needs a quantity.
      setQtyModalOpen(true);
    }
  };

  const onCancelBinWarning = () => {
    // No - discard the previewed scan; nothing is added.
    setBinWarningOpen(false);
    setPendingItem(null);
    setPendingBarcode("");
    setPendingQty("");
    setPendingMode("scan");
    searchBoxInputRef.current?.focus();
  };

  const doScan = useCallback(
    (barcode: string): void => {
      if (!barcode || barcode.trim() === "") {
        setSearchTerm("");
        errorSound.play();
        toast.error("Barcode must have a value", { autoClose: 1000 });
        return;
      }

      const trimmedBarcode = barcode.trim();
      putAwayService
        // commit=false: a stock-code scan is previewed (not added) so the user can confirm the qty first.
        .scanPutAwayItemAPI(trimmedBarcode, dataStore.serverUrl || "", dataStore.dbKey || "", true, true, false, undefined, selectedLocation?.locationNo)
        .then((scanResponse: PutAwayScanResponse) => {
          if (!scanResponse || scanResponse.hasError) {
            setSearchTerm("");
            errorSound.play();
            const errMsg = scanResponse && scanResponse.errorMessage ? scanResponse.errorMessage : "Bin not found";
            toast.error(errMsg, { autoClose: 3000 });
            searchBoxInputRef.current?.focus();
            return;
          }
          setSearchTerm("");
          successSound.play();
          // For a stock code scan the backend only previewed the item (commit=false),
          // so nothing has been added yet. Show a popup with the bin/stock details and
          // have the user enter a qty; we commit on confirm and add nothing on close.
          if (scanResponse.successCode === 3 && scanResponse.lastScan) {
            const scanned = scanResponse.lastScan;
            setPendingItem(scanned);
            setPendingBarcode(trimmedBarcode);
            setPendingMode("scan");
            setPendingQty("");
            // If the target bin already has a different stock item in the list, warn first -
            // unless the user already confirmed mixing stockcodes for this bin.
            const binAcknowledged = !!scanned.binCode && acknowledgedBinRef.current === scanned.binCode;
            const binHasDifferentStock = !!currentData?.rows?.some(
              (r) => r.binCode === scanned.binCode && r.stockNo !== scanned.stockNo,
            );
            if (BIN_MIXED_STOCK_WARNING_ENABLED && binHasDifferentStock && !binAcknowledged) {
              setBinWarningOpen(true);
            } else {
              setQtyModalOpen(true);
            }
            return;
          }
          // A serial was scanned (also previewed, not added). Warn if it mixes a different
          // stockcode into the bin. A serial with a default qty commits straight away; one with
          // no default qty (0) shows the qty popup so the user can choose the quantity.
          if (scanResponse.successCode === 2 && scanResponse.lastScan) {
            const scanned = scanResponse.lastScan;
            const binAcknowledged = !!scanned.binCode && acknowledgedBinRef.current === scanned.binCode;
            const binHasDifferentStock = !!currentData?.rows?.some(
              (r) => r.binCode === scanned.binCode && r.stockNo !== scanned.stockNo,
            );
            const needsQty = !scanned.qty;
            if (BIN_MIXED_STOCK_WARNING_ENABLED && binHasDifferentStock && !binAcknowledged) {
              setPendingItem(scanned);
              setPendingBarcode(trimmedBarcode);
              setPendingMode("scan");
              setPendingQty("");
              setBinWarningOpen(true);
            } else if (needsQty) {
              setPendingItem(scanned);
              setPendingBarcode(trimmedBarcode);
              setPendingMode("scan");
              setPendingQty("");
              setQtyModalOpen(true);
            } else {
              commitScan(trimmedBarcode);
            }
            return;
          }
          // A new bin was scanned (put-to bin changed) - reset the acknowledged bin.
          if (scanResponse.successCode === 1) {
            acknowledgedBinRef.current = "";
          }
          const successMsg = scanResponse && scanResponse.successMessage ? scanResponse.successMessage : "Scanned successfully";
          toast.success(successMsg, { autoClose: 1000 });
          return refreshItems().then(() => {
            searchBoxInputRef.current?.focus();
          });
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
          searchBoxInputRef.current?.focus();
        });
    },
    [dataStore.dbKey, dataStore.serverUrl, refreshItems, currentData, commitScan, selectedLocation],
  );

  const onBarcodeScanned = useCallback(
    (barcode: string): void => {
      doScan(barcode);
    },
    [doScan],
  );

  const onKeyPressed = useCallback(
    (event: any) => {
      // if (event.target && event.target instanceof HTMLInputElement &&
      //     event.target.name === searchBoxInputRef.current!.name) {
      //         return;
      // }
      if (event.target && event.target instanceof HTMLInputElement) {
        return;
      }
      const keyPressed = event.key ? event.key : "";
      if (keyPressed === "") {
        return;
      }
      if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
        event.preventDefault();
        setSearchTerm((oldState) => {
          const newState = oldState;
          onBarcodeScanned(newState);
          return newState;
        });
      } else if (keyPressed.length === 1) {
        setSearchTerm((oldState) => {
          const newState = oldState + keyPressed;
          return newState;
        });
      }
    },
    [onBarcodeScanned],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyPressed);
    return () => {
      window.removeEventListener("keydown", onKeyPressed);
    };
  }, [onKeyPressed]);

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      const hasUnsavedChanges = currentData?.rows && currentData.rows.length > 0;
      if (!hasUnsavedChanges) {
        return;
      } else if (!window.confirm("Current order has not been fully scanned. Leave page ?")) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [currentData]);

  // useKeyDown((event: any) => {
  //     onKeyPressed(event);
  // }, []);

  let shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => !!currentData?.rows && currentData.rows.length > 0 && currentLocation.pathname !== nextLocation.pathname,
    [currentData],
  );
  let blocker = useBlocker(shouldBlock);

  // Reset the blocker if the user cleans the form
  useEffect(() => {
    if (blocker.state === "blocked" && (!currentData?.rows || currentData.rows.length === 0)) {
      blocker.reset();
    } else if (blocker.state === "blocked" && !!currentData?.rows && currentData.rows.length > 0) {
      //show modal depending on blocker status
      setModalOpen(true);
    } else if (blocker.state !== "blocked") {
      setModalOpen(false);
    }
  }, [blocker, currentData]);

  const submitStockCount = useCallback(async (): Promise<void> => {
    if (!currentData?.rows || currentData.rows.length === 0) {
      toast.error("List is empty and was not submitted", { autoClose: 1000 });
      setSubmitDisabled(false);
      return;
    }
    if (!selectedLocation) {
      toast.error("Please select a location before submitting", { autoClose: 1500 });
      setSubmitDisabled(false);
      return;
    }
    try {
      const stockCountResponse: TransHeaderResponse = await putAwayService.submitStockCountAPI(
        dataStore.serverUrl || "",
        dataStore.dbKey || "",
        selectedLocation.locationNo,
        batchDate,
        batchDescription,
      );
      if (stockCountResponse) {
        if (stockCountResponse.errorMessage) {
          toast.error(stockCountResponse.errorMessage, { autoClose: false });
        } else {
          const batchInfo = stockCountResponse.transRefCode ? ` (Batch ${stockCountResponse.transRefCode})` : "";
          toast.success(`Stock count submitted successfully${batchInfo}`, { autoClose: 2000 });
          setSubmitDisabled(false);
          setCurrentData(null);
          navigate("/menu");
        }
      } else {
        toast.error("There was an error submitting", { autoClose: false });
        setSubmitDisabled(false);
      }
    } catch (error: any) {
      toast.error(error.message || error, { autoClose: false });
      setSubmitDisabled(false);
    }
  }, [dataStore.dbKey, dataStore.serverUrl, currentData, navigate, selectedLocation, batchDate, batchDescription]);

  const cancelStockCount = useCallback(async (): Promise<void> => {
    try {
      setSubmitDisabled(true);
      const resp: BooleanResponse = await putAwayService.deletePutAwayListAPI(dataStore.serverUrl || "", dataStore.dbKey || "");
      if (resp) {
        if (resp.hasError) {
          toast.error(resp.errorMessage, { autoClose: false });
        } else if (resp.isTrue) {
          toast.success("Cancelled", { autoClose: 2000 });
        } else if (!resp.isTrue) {
          toast.error("There was an error cancelling", { autoClose: false });
        }
        await refreshItems();
        setSubmitDisabled(false);
        return navigate("/menu");
      }
    } catch (error: any) {
      toast.error(error.message || error, { autoClose: false });
      setSubmitDisabled(false);
    }
  }, [dataStore.serverUrl, dataStore.dbKey, refreshItems, navigate]);

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onSearchClicked();
    }
  };
  const handleDeleteItem = (stockNo: number) => {
    if (!currentData?.rows || currentData.rows.length === 0 || stockNo === 0) {
      return;
    }
    // find the item to delete
    const itemToDelete = currentData.rows.find((item) => item.stockNo === stockNo);
    if (!itemToDelete) {
      return;
    }
    //show confirmation dialog
    if (!window.confirm(`Are you sure you want to delete ${itemToDelete.stockDesc} [${itemToDelete.stockNo}] from the list?`)) {
      return;
    }
    //call delete api
    putAwayService
      .deletePutAwayItemAPI(stockNo, dataStore.serverUrl || "", dataStore.dbKey || "")
      .then((resp: BooleanResponse) => {
        if (resp) {
          if (resp.hasError) {
            toast.error(resp.errorMessage, { autoClose: false });
          } else if (resp.isTrue) {
            toast.success("Item deleted", { autoClose: 2000 });
            refreshItems();
          } else if (!resp.isTrue) {
            toast.error("There was an error deleting the item", { autoClose: false });
            refreshItems();
          }
        }
      })
      .catch((error: any) => {
        toast.error(error.message || error, { autoClose: false });
      });
  };

  const onSearchClicked = () => {
    onBarcodeScanned(searchTerm);
  };

  const onSubmit = () => {
    setSubmitDisabled(true);
    submitStockCount();
    setTimeout(() => {
      setSubmitDisabled(false);
    }, 3000);
  };

  const onCancel = async () => {
    await cancelStockCount();
  };
  const onCloseModal = () => {
    setModalOpen(false);
  };

  let submitButtonCssClass = "btn btn-lg btn-success btn-block fill-half";
  if (!currentData?.rows || currentData.rows.length === 0) {
    submitButtonCssClass += " disabled";
  }
  let cancelButtonCssClass = "btn btn-lg btn-secondary btn-block fill-half";
  if (!currentData?.lastBinCode && (!currentData?.rows || currentData.rows.length === 0)) {
    cancelButtonCssClass += " disabled";
  }
  return (
    <>
      <div className="container-limited-width">
        <div className="row">
          <div className="col-12 p-2">
            <div className="d-flex align-items-center justify-content-between">
              <span>
                Location:&nbsp;
                <span className="fw-bold">{selectedLocation ? `${selectedLocation.locationCode} - ${selectedLocation.locationDescription}` : "None selected"}</span>
              </span>
              <Button variant="outline-primary" size="sm" onClick={onChangeLocation}>
                Change
              </Button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 p-2">
            <label htmlFor="batchDate" className="form-label mb-1">
              Date
            </label>
            <input id="batchDate" name="batchDate" type="date" className="form-control" value={batchDate} onChange={(e) => setBatchDate(e.target.value)} />
          </div>
        </div>
        <div className="row">
          <div className="col-12 p-2">
            <label htmlFor="batchDescription" className="form-label mb-1">
              Batch description
            </label>
            <input
              id="batchDescription"
              name="batchDescription"
              type="text"
              className="form-control"
              placeholder="Enter batch description"
              autoCapitalize="none"
              value={batchDescription}
              onChange={(e) => setBatchDescription(e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 p-2">
            <div className="input-group">
              <input
                name="searchBox"
                type="text"
                className="form-control search-box"
                placeholder={currentData?.lastBinCode ? "Scan barcode" : "Scan bin"}
                autoCapitalize="none"
                aria-describedby="btnGroupAddon"
                onKeyUp={handleKeyUp}
                onChange={handleSearchChange}
                value={searchTerm}
                ref={searchBoxInputRef}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                title="Show keyboard"
                aria-label="Show keyboard"
                onClick={() => searchBoxInputRef.current?.focus()}
              >
                <i className="fa fa-keyboard-o" aria-hidden="true"></i>
              </button>
            </div>
            {scanWarningCount !== null && (currentData?.rows?.length || 0) >= scanWarningCount && (
              <div className="text-danger fw-bold mt-1">
                You have scanned {currentData?.rows?.length}, please submit.
              </div>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 pl-1 pr-1">
            <ul className="list-group">
              <li
                className="list-group-item d-flex flex-row justify-content-center align-items-center"
                style={{ backgroundColor: currentData?.lastBinCode ? "rgb(128, 255, 128)" : "#d3d3d3" }}
              >
                Bin:&nbsp;<span className="fw-bold">{currentData?.lastBinCode || ""}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 pl-1 pr-1">
            <ul className="list-group">
              {currentData?.rows &&
                [...currentData.rows].sort((a, b) => (b.id || 0) - (a.id || 0)).map((product, index) => {
                  return (
                    <li key={`${product.id ?? product.stockNo}_${index}`} className="list-group-item d-flex flex-row flex-grow-1 align-self-stretch wide-tile">
                      <div className="d-flex flex-column justify-content-between align-items-start flex-grow-1 align-self-stretch">
                        <div className="d-flex fw-bold flex-row">
                          {product.stockDesc} [{product.stockNo}]
                        </div>
                        <div className="d-flex flex-row justify-content-between flex-grow-1 align-self-stretch">
                          <div className="d-flex fill-half">StockCode: {product.stockCode}</div>
                          <div className="d-flex fill-half justify-content-end">From: {product.fromBinCode}</div>
                        </div>
                        <div className="d-flex flex-row justify-content-between flex-grow-1 align-self-stretch">
                          <div className="d-flex justify-content-start">Serial: {product.serialNo}</div>
                          <div className="d-flex justify-content-center align-items-center">
                            Qty:&nbsp;
                            {isQtyEditable(product.stockSerialMode) ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary qty-input"
                                onClick={() => openEditQty(product)}
                                title="Edit quantity"
                              >
                                {product.qty || 0}
                              </button>
                            ) : (
                              <span>{product.qty || 0}</span>
                            )}
                          </div>
                          <div className="d-flex justify-content-end">To: {product.binCode}</div>
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-center justify-content-between">
                        <span className="badge bg-secondary count-no">{(currentData?.rows?.length || 0) - index}</span>
                        <Button
                          variant="link"
                          className="text-danger"
                          onClick={() => {
                            handleDeleteItem(product.stockNo || 0);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </div>
                    </li>
                  );
                })}
            </ul>
            {submitDisabled && <p className="text-center">Please wait...</p>}
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 mt-2 pl-1 pr-1">
            <div className="d-flex gap-2 justify-space-between">
              <button disabled={submitDisabled || !currentData?.rows || currentData.rows.length === 0} className={submitButtonCssClass} type="button" onClick={onSubmit}>
                {submitDisabled ? "Please wait..." : "Submit"}
              </button>
              <button className={cancelButtonCssClass} type="button" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal show={modalOpen} onHide={onCloseModal} backdrop="static" keyboard={false} aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header>
          <Modal.Title>Items were scanned but not submitted !</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you would like to leave without submitting ?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => blocker.proceed?.()}>
            Leave
          </Button>
          <Button variant="success" onClick={() => blocker.reset?.()}>
            Stay
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={locationModalOpen} onHide={() => setLocationModalOpen(false)} backdrop="static" keyboard={false} aria-labelledby="location-modal-title" centered>
        <Modal.Header>
          <Modal.Title>Select a location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {locations.length === 0 ? (
            <p className="text-center mb-0">No locations available.</p>
          ) : (
            <ul className="list-group">
              {locations.map((loc) => (
                <li key={loc.locationNo} className="list-group-item list-group-item-action" role="button" onClick={() => handleSelectLocation(loc)}>
                  <span className="fw-bold">{loc.locationCode}</span> - {loc.locationDescription}
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLocationModalOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={binWarningOpen} onHide={onCancelBinWarning} backdrop="static" keyboard={false} aria-labelledby="bin-warning-title" centered>
        <Modal.Header>
          <Modal.Title id="bin-warning-title">Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Scanned multiple stockcodes in bin <span className="fw-bold">{pendingItem?.binCode}</span>.
          </p>
          <p className="mb-0">Are you sure?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancelBinWarning}>
            No
          </Button>
          <Button variant="warning" onClick={onConfirmBinWarning}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={qtyModalOpen}
        onHide={onCloseQty}
        backdrop="static"
        keyboard={false}
        aria-labelledby="qty-modal-title"
        centered
        onEntered={() => qtyInputRef.current?.focus()}
      >
        <Modal.Header>
          <Modal.Title id="qty-modal-title">{pendingMode === "edit" ? "Edit quantity" : "Confirm item"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="list-group mb-3">
            <li className="list-group-item d-flex justify-content-between align-items-center" style={{ backgroundColor: "rgb(128, 255, 128)" }}>
              Bin:&nbsp;<span className="fw-bold">{pendingItem?.binCode || currentData?.lastBinCode || ""}</span>
            </li>
          </ul>
          <div className="mb-1">
            <span className="fw-bold">
              {pendingItem?.stockDesc} [{pendingItem?.stockNo}]
            </span>
          </div>
          <div className="d-flex flex-row justify-content-between">
            <div>StockCode: {pendingItem?.stockCode}</div>
            <div>From: {pendingItem?.fromBinCode}</div>
          </div>
          {pendingItem?.serialNo ? <div>Serial: {pendingItem.serialNo}</div> : null}
          <div className="mt-3">
            <label htmlFor="pendingQty" className="form-label mb-1">
              Quantity
            </label>
            <input
              id="pendingQty"
              name="pendingQty"
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min={1}
              className="form-control"
              placeholder="Enter quantity"
              value={pendingQty}
              ref={qtyInputRef}
              onChange={(e) => setPendingQty(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onConfirmQty();
                }
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCloseQty}>
            Close
          </Button>
          <Button variant="success" onClick={onConfirmQty}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StockCountPage;
